// UnifiedDetailsDialog.jsx — Multi-Recipient BlitzPay + Transfer/Withdrawal + BBC Split Support
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, Button, Typography, Box, Alert, CircularProgress, IconButton,
  Paper, Chip, Divider, Grid, Table, TableContainer, TableBody, TableRow,
  TableCell, useMediaQuery, Collapse, Stack, Avatar, Card, CardContent,
  Fade, TextField, Snackbar, List, ListItem, ListItemAvatar, ListItemText,
} from "@mui/material";
import {
  Close as CloseIcon, Receipt as ReceiptIcon, Payment as PaymentIcon,
  Person as PersonIcon, Business as BusinessIcon, Speed as SpeedIcon,
  Schedule as ScheduleIcon, SwapHoriz as SwapHorizIcon, TrendingUp as TrendingUpIcon,
  Info as InfoIcon, CheckCircle as CheckCircleIcon, Warning as WarningIcon,
  AccessTime as AccessTimeIcon, AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon, Group as GroupIcon, AttachMoney as AttachMoneyIcon,
  Description as DescriptionIcon, ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon, OpenInNew as OpenInNewIcon, Edit as EditIcon,
  Save as SaveIcon, Cancel as CancelIcon, Assignment as AssignmentIcon,
  ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon, Link as LinkIcon,
  People as PeopleIcon, Bolt as BoltIcon, CallSplit as CallSplitIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import client from "../../../API";

const currency = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
});

// ─────────────────────────────────────────────────────────────────────────────
// BBC SPLIT COLOR TOKENS  (mirrors UnifiedActivityCard exactly)
// ─────────────────────────────────────────────────────────────────────────────

// Yellow-leaning chartreuse — incoming BBC split
const YG        = "#8C7A0A";
const YG_LIGHT  = "#F0DC68";
const YG_BG     = "#FEFBE8";
const YG_DARK   = "#6E6008";
const YG_DEEP   = "#504505";
const YG_INK    = "#1A1400";
const YG_TINT   = alpha("#8C7A0A", 0.07);
const YG_BORDER = alpha("#8C7A0A", 0.35);

// Amber — outgoing BBC split
const Y        = "#CA8A04";
const Y_LIGHT  = "#FDE68A";
const Y_DARK   = "#A16207";
const Y_DEEP   = "#78350F";
const Y_INK    = "#1C0A00";
const Y_TINT   = alpha("#CA8A04", 0.07);
const Y_BORDER = alpha("#CA8A04", 0.35);

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const isBBCSplit = (item) =>
  item?.invoice_type === "bbc" || item?.source_type === "bbc" || item?.isBBC;

const categorizeItem = (item) => {
  // BBC Split must be checked first (before generic incoming/blitzpay)
  if (item.isIncoming && isBBCSplit(item))  return "bbc-split-incoming";
  if (!item.isIncoming && isBBCSplit(item)) return "bbc-split-outgoing";
  if (
    item.recordType === "transfer" ||
    item.type === "Internal Transfer" ||
    item.transaction_type === "internal_transfer"
  )
    return "transfer";
  if (item.recordType === "withdrawal" || item.type === "Withdrawal")
    return "withdrawal";
  if (item.type === "Payout" || item.recordType === "payout") return "payout";
  if (item.isIncoming) return "invoice-incoming";
  if (
    item.isBlitzPay ||
    item.recordType === "blitzpay-invoice" ||
    item.invoice_type === "blitzpay" ||
    item.source_type === "blitzpay"
  )
    return "blitzpay";
  return "invoice-outgoing";
};

const getTypeConfig = (itemType) => {
  switch (itemType) {
    case "bbc-split-incoming":
      return {
        label: "Split Received",
        color: "warning",
        icon: <CallSplitIcon />,
        bgColor: () => YG_TINT,
        accent: { main: YG, light: YG_LIGHT, bg: YG_BG, dark: YG_DARK, deep: YG_DEEP, ink: YG_INK, tint: YG_TINT, border: YG_BORDER },
        pillBg: YG_LIGHT,
        pillInk: YG_INK,
      };
    case "bbc-split-outgoing":
      return {
        label: "Split Sent",
        color: "warning",
        icon: <BoltIcon />,
        bgColor: () => Y_TINT,
        accent: { main: Y, light: Y_LIGHT, dark: Y_DARK, deep: Y_DEEP, ink: Y_INK, tint: Y_TINT, border: Y_BORDER },
        pillBg: Y_LIGHT,
        pillInk: Y_INK,
      };
    case "transfer":
      return { label: "Internal Transfer", color: "info",      icon: <SwapHorizIcon />, bgColor: (t) => alpha(t.palette.info.main, 0.1), accent: null };
    case "withdrawal":
      return { label: "Withdrawal",        color: "error",     icon: <ArrowUpIcon />,   bgColor: (t) => alpha(t.palette.error.main, 0.1), accent: null };
    case "payout":
      return { label: "Payout",            color: "primary",   icon: <PaymentIcon />,   bgColor: (t) => alpha(t.palette.primary.main, 0.1), accent: null };
    case "blitzpay":
      return { label: "BlitzPay Invoice",  color: "info",      icon: <SwapHorizIcon />, bgColor: (t) => alpha(t.palette.info.main, 0.1), accent: null };
    case "invoice-incoming":
      return { label: "Incoming Invoice",  color: "success",   icon: <TrendingUpIcon />, bgColor: (t) => alpha(t.palette.success.main, 0.1), accent: null };
    case "invoice-outgoing":
      return { label: "Outgoing Invoice",  color: "secondary", icon: <ReceiptIcon />,   bgColor: (t) => alpha(t.palette.secondary.main, 0.1), accent: null };
    default:
      return { label: "Unknown",           color: "default",   icon: <ReceiptIcon />,   bgColor: (t) => alpha(t.palette.grey[500], 0.1), accent: null };
  }
};

// BBC status pill — matches UnifiedActivityCard's getBBCStatusPillProps
const getBBCStatusPill = (status, isOutgoing) => {
  const s = (status || "").toLowerCase();
  let label, bg, fg, border;
  if (isOutgoing) {
    if (s === "pending")                { label = "Awaiting Approval";   bg = Y_TINT;  fg = Y_DEEP;  border = Y_BORDER;  }
    else if (s === "approved")          { label = "Approved · Electing"; bg = alpha("#16A34A", 0.07); fg = "#14532D"; border = alpha("#16A34A", 0.30); }
    else if (s === "paid" || s === "funded") { label = "Funded & Settled";    bg = alpha("#16A34A", 0.07); fg = "#14532D"; border = alpha("#16A34A", 0.30); }
    else if (s === "cancelled")         { label = "Cancelled";            bg = alpha("#DC2626", 0.07); fg = "#7F1D1D"; border = alpha("#DC2626", 0.28); }
    else                                { label = status;                 bg = Y_TINT;  fg = Y_DEEP;  border = Y_BORDER;  }
  } else {
    if (s === "pending")                { label = "Action Required";     bg = YG_TINT; fg = YG_DEEP; border = YG_BORDER; }
    else if (s === "approved")          { label = "Approved · Settling"; bg = alpha("#16A34A", 0.07); fg = "#14532D"; border = alpha("#16A34A", 0.30); }
    else if (s === "paid" || s === "funded") { label = "Funded";              bg = alpha("#16A34A", 0.07); fg = "#14532D"; border = alpha("#16A34A", 0.30); }
    else if (s === "cancelled")         { label = "Cancelled";            bg = alpha("#DC2626", 0.07); fg = "#7F1D1D"; border = alpha("#DC2626", 0.28); }
    else                                { label = status;                 bg = YG_TINT; fg = YG_DEEP; border = YG_BORDER; }
  }
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", px: 1, height: 24,
      borderRadius: "6px", bgcolor: bg, border: `1px solid ${border}`,
    }}>
      <Typography sx={{ fontWeight: 700, color: fg, fontSize: "0.72rem", lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );
};

const getStatusChip = (status, small = false, itemType = "") => {
  const s = (status || "").toLowerCase();

  // BBC types get their custom pill
  if (itemType === "bbc-split-incoming") return getBBCStatusPill(status, false);
  if (itemType === "bbc-split-outgoing") return getBBCStatusPill(status, true);

  const cfg = {
    pending: "warning", approved: "info", completed: "success",
    paid: "success", funded: "success", cancelled: "error",
    declined: "error", processing: "warning",
  };
  return (
    <Chip
      label={status}
      size="small"
      color={cfg[s] || "default"}
      sx={{
        fontWeight: 600,
        textTransform: "capitalize",
        height: small ? 20 : 24,
        fontSize: small ? "0.7rem" : "0.75rem",
      }}
    />
  );
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const getInvoiceRoute = (item, itemType) => {
  if (["payout", "transfer", "withdrawal", "bbc-split-incoming", "bbc-split-outgoing"].includes(itemType)) return null;
  const status = (item.status || item.invoice_status || "").toLowerCase();
  const base =
    item.invoice_type === "creator" || itemType === "invoice-incoming"
      ? `/creatorinvoice/${item.id}`
      : `/invoicing/${item.id}`;
  return status === "pending" ? `${base}?needsApproval=true` : base;
};

// ─────────────────────────────────────────────────────────────────────────────
// FEE PAYER DERIVATION
// ─────────────────────────────────────────────────────────────────────────────

const deriveFeePayer = (item) => {
  const firstPayout = item.payoutDetailsArray?.[0];
  const explicit =
    item.fee_payer ||
    item.custom_details?.fee_payer ||
    item.feeBreakdown?.feePayer ||
    item.payoutDetails?.fee_payer ||
    firstPayout?.fee_payer;
  if (explicit) return explicit;
  const role = firstPayout?.role || item.payoutDetails?.role || item.role;
  if (role === "Client")  return "me";
  if (role === "Creator") return "creator";
  const rate =
    firstPayout?.interest_rate != null   ? firstPayout.interest_rate
    : item.payoutDetails?.interest_rate != null ? item.payoutDetails.interest_rate
    : item.interest_rate;
  if (rate != null) return Number(rate) === 0 ? "me" : "creator";
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// ENTITY INFO — mirrors UnifiedActivityCard's getEntityInfo
// ─────────────────────────────────────────────────────────────────────────────

const getEntityInfo = (item, itemType) => {
  if (itemType === "bbc-split-incoming") {
    const senderName = item.client_name || item.company_name || item.name || "Agency";
    const recipientCount = item.creator_payments?.length || item.recipient_count || item.custom_details?.recipient_count || null;
    const preCount = item.pre_accepted_count || (item.creator_payments || []).filter((p) => p.preapproved || p.blitzpay_preapproved || p.blitzpay).length || 0;
    return {
      name: senderName,
      subtitle: recipientCount
        ? `${recipientCount} contractor${recipientCount !== 1 ? "s" : ""}${preCount > 0 ? ` · ${preCount} pre-accepted ⚡` : ""}`
        : "Blitz Split invoice",
      preCount,
      recipientCount,
    };
  }
  if (itemType === "bbc-split-outgoing") {
    const clientName = item.client_name || item.name || "Enterprise Client";
    const recipientCount = item.recipient_count || item.custom_details?.recipient_count || null;
    const s = (item.status || "").toLowerCase();
    const statusLabel =
      s === "approved" ? "Approved — awaiting funding"
      : (s === "paid" || s === "funded") ? "Funded"
      : "Awaiting client approval";
    return {
      name: clientName,
      subtitle: recipientCount ? `${recipientCount} contractor${recipientCount !== 1 ? "s" : ""} · ${statusLabel}` : statusLabel,
      recipientCount,
    };
  }
  if (itemType === "transfer")
    return { name: item.name || "Balance Transfer", subtitle: item.description || "Internal transfer" };
  if (itemType === "withdrawal")
    return { name: item.name || `Withdrawal to ${item.payment_service || "Bank"}`, subtitle: item.description || "Bank transfer" };
  if (itemType === "payout") {
    const ci = item.creator_info || {};
    const id = item.creator_id || ci.creator_id;
    return { name: ci.display_name || item.name || id || "Creator", subtitle: id ? `@${id}` : null, email: ci.email || ci.payout_email };
  }
  if (itemType === "blitzpay") {
    const isMulti = item.isMultiRecipient && item.recipientCount > 1;
    if (isMulti) {
      const payouts = item.payoutDetailsArray || [];
      const names = payouts.map((pd) => pd.name || pd.creator_display_name || pd.creator_id || null).filter(Boolean);
      const MAX = 2;
      const displayName = names.length === 0
        ? (item.campaign_name || "BlitzPay to Multiple Creators")
        : names.length <= MAX
          ? `BlitzPay to ${names.join(", ")}`
          : `BlitzPay to ${names.slice(0, MAX).join(", ")} +${names.length - MAX} more`;
      return { name: displayName, subtitle: `${item.recipientCount} creators` };
    }
    const displayName = item.campaign_name || item.name || "BlitzPay Payment";
    if (item.payoutDetails) {
      const creatorId = item.payoutDetails.creator_id;
      const creatorName = item.payoutDetails.creator_info?.display_name || creatorId;
      return { name: displayName, subtitle: creatorName ? `@${creatorName}` : "Creator payment" };
    }
    return { name: displayName, subtitle: "Creator payment" };
  }
  if (itemType === "invoice-incoming")
    return { name: item.client_name || item.name || "Vendor", subtitle: item.creator_id ? `@${item.creator_id}` : null };
  return { name: item.client_name || item.name || "Client", subtitle: item.creator_id ? `@${item.creator_id}` : null };
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const InfoRow = ({ label, value, icon, highlight = false, isMobile, accentColor }) => (
  <TableRow sx={{ "&:last-child td": { border: 0 } }}>
    <TableCell
      sx={{
        fontWeight: 600, color: "text.secondary", width: "40%",
        py: isMobile ? 1 : 1.5, fontSize: isMobile ? "0.8rem" : "0.875rem",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {React.cloneElement(icon, { sx: { fontSize: isMobile ? 18 : 20, ...(accentColor && { color: accentColor }) } })}
        {label}
      </Box>
    </TableCell>
    <TableCell sx={{ py: isMobile ? 1 : 1.5 }}>
      {highlight ? (
        <Typography variant="body2" fontWeight={700} sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem", color: accentColor || "primary.main" }}>
          {value}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}>
          {value}
        </Typography>
      )}
    </TableCell>
  </TableRow>
);

const CollapsibleSection = ({ title, icon, children, defaultExpanded = true, isMobile, accentColor }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2, overflow: "hidden", borderRadius: 2,
        ...(accentColor && { borderColor: alpha(accentColor, 0.35) }),
      }}
    >
      <Box
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          p: isMobile ? 1.5 : 2,
          bgcolor: accentColor ? alpha(accentColor, 0.05) : "grey.50",
          cursor: "pointer",
          "&:hover": { bgcolor: accentColor ? alpha(accentColor, 0.09) : "grey.100" },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {React.cloneElement(icon, { sx: { fontSize: isMobile ? 20 : 22, color: accentColor || "text.secondary" } })}
          <Typography variant={isMobile ? "body2" : "subtitle2"} fontWeight={600} sx={{ color: accentColor ? alpha(accentColor, 0.85) : "text.primary" }}>
            {title}
          </Typography>
        </Box>
        {expanded
          ? <ExpandLessIcon sx={{ fontSize: isMobile ? 18 : 20, color: accentColor || "text.secondary" }} />
          : <ExpandMoreIcon sx={{ fontSize: isMobile ? 18 : 20, color: accentColor || "text.secondary" }} />}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: isMobile ? 1.5 : 2 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BBC CONTEXT BANNER
// ─────────────────────────────────────────────────────────────────────────────

const BBCContextBanner = ({ item, itemType }) => {
  const status = (item.status || "").toLowerCase();
  const isIncoming = itemType === "bbc-split-incoming";
  const isOutgoing = itemType === "bbc-split-outgoing";
  if (!isIncoming && !isOutgoing) return null;

  const accentMain   = isIncoming ? YG : Y;
  const accentDeep   = isIncoming ? YG_DEEP : Y_DEEP;
  const accentTint   = isIncoming ? YG_TINT : Y_TINT;
  const accentBorder = isIncoming ? YG_BORDER : Y_BORDER;
  const Icon = isIncoming ? CallSplitIcon : SettingsIcon;

  const text = (() => {
    if (isIncoming) {
      if (status === "pending")                    return "Your approval commits payment. Contractors see their shares immediately.";
      if (status === "approved")                   return "Approved — contractors can elect instant payout. Funds settle at payment term.";
      return "Blitz Split invoice from your vendor.";
    }
    if (status === "pending")                       return "Waiting for client approval. Contractors see amounts once approved.";
    if (status === "approved")                      return "Client approved — contractors can now elect ⚡ instant payout.";
    if (status === "paid" || status === "funded")   return "Fully funded. All contractors have been paid or settled.";
    return "Manage your Split roster and payout elections.";
  })();

  return (
    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: accentTint, border: `1px solid ${accentBorder}`, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Icon sx={{ fontSize: 15, color: accentMain, mt: "2px", flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: accentDeep, fontWeight: 700, lineHeight: 1.55, display: "block" }}>
          {text}
        </Typography>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BBC ROSTER SECTION (inlined from BBCSplitApprovalPage roster logic)
// ─────────────────────────────────────────────────────────────────────────────

const BBCRosterSection = ({ item, itemType, isMobile }) => {
  const isIncoming = itemType === "bbc-split-incoming";
  const accentMain   = isIncoming ? YG : Y;
  const accentDeep   = isIncoming ? YG_DEEP : Y_DEEP;
  const accentTint   = isIncoming ? YG_TINT : Y_TINT;
  const accentBorder = isIncoming ? YG_BORDER : Y_BORDER;
  const accentLight  = isIncoming ? YG_LIGHT : Y_LIGHT;
  const accentInk    = isIncoming ? YG_INK : Y_INK;

  const payments = item.creator_payments || item.payouts || item.payoutDetailsArray || [];
  if (payments.length === 0) return null;

  const preCount = payments.filter((p) => p.preapproved || p.blitzpay_preapproved || p.blitzpay).length;
  const total = payments.reduce((s, p) => s + parseFloat(p.amount || p.net_amount || 0), 0);

  return (
    <CollapsibleSection
      title={`Contractor Roster (${payments.length})`}
      icon={<PeopleIcon />}
      defaultExpanded={true}
      isMobile={isMobile}
      accentColor={accentMain}
    >
      <Stack spacing={0.75}>
        {payments.map((p, i) => {
          const name = p.recipient_name || p.creator_name || p.creator_id || p.recipient_email || p.name || "Contractor";
          const pre  = p.preapproved || p.blitzpay_preapproved || p.blitzpay;
          const amt  = parseFloat(p.amount || p.net_amount || 0);
          return (
            <Box
              key={p.id || i}
              sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                py: 1.25, px: 1.5, borderRadius: 2,
                bgcolor: pre ? accentTint : "transparent",
                border: "1px solid",
                borderColor: pre ? accentBorder : "divider",
              }}
            >
              <Avatar sx={{ width: 30, height: 30, fontSize: "0.72rem", fontWeight: 800, bgcolor: pre ? accentLight : "grey.200", color: pre ? accentInk : "text.secondary", flexShrink: 0 }}>
                {name.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} noWrap>{name}</Typography>
                {p.recipient_email && p.recipient_email !== name && (
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }} noWrap>{p.recipient_email}</Typography>
                )}
              </Box>
              <Stack direction="row" spacing={0.75} alignItems="center">
                {pre && (
                  <Chip
                    icon={<BoltIcon sx={{ fontSize: "0.7rem !important" }} />}
                    label="BlitzPay ⚡"
                    size="small"
                    sx={{ height: 20, fontSize: "0.62rem", fontWeight: 800, bgcolor: accentLight, color: accentInk, "& .MuiChip-icon": { color: accentInk } }}
                  />
                )}
                <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums", minWidth: 60, textAlign: "right", color: accentDeep }}>
                  {currency.format(amt)}
                </Typography>
              </Stack>
            </Box>
          );
        })}
        {total > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1.25, mt: 0.5, borderTop: "1px solid", borderColor: accentBorder, px: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>Contractor total</Typography>
            <Typography variant="body2" sx={{ fontWeight: 900, fontVariantNumeric: "tabular-nums", color: accentDeep }}>
              {currency.format(total)}
            </Typography>
          </Box>
        )}
      </Stack>
      {preCount > 0 && (
        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}>
          <BoltIcon sx={{ fontSize: 14, color: accentMain }} />
          <Typography variant="caption" sx={{ color: accentDeep, fontWeight: 600, fontSize: "0.72rem" }}>
            {preCount} contractor{preCount !== 1 ? "s" : ""} pre-accepted instant payout
          </Typography>
        </Box>
      )}
    </CollapsibleSection>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FEE BREAKDOWN DISPLAY
// ─────────────────────────────────────────────────────────────────────────────

const FeeBreakdownBlock = ({ item, isMulti, feePayer, isMobile }) => {
  const fb = item.feeBreakdown;
  if (!fb?.isFunded) return null;

  const youPaidFee     = feePayer === "me";
  const creatorPaidFee = feePayer === "creator";
  const feeKnown       = feePayer !== null;

  const invoiceAmount  = fb.invoiceAmount;
  const platformFee    = fb.platformFee;
  const totalCharge    = fb.totalCharge ?? (invoiceAmount + platformFee);

  const creatorsNetAmount = (() => {
    if (item.totalPayoutNetAmount > 0) return item.totalPayoutNetAmount;
    if (youPaidFee)     return invoiceAmount;
    if (creatorPaidFee) return invoiceAmount - platformFee;
    return invoiceAmount;
  })();

  const clientPays = youPaidFee ? totalCharge : invoiceAmount;
  const fsPrimary   = isMobile ? "0.8rem"  : "0.875rem";
  const fsSecondary = isMobile ? "0.75rem" : "0.8rem";

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AttachMoneyIcon fontSize="small" color="warning" />
        Fee Breakdown
      </Typography>
      <Stack spacing={1.5}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: fsPrimary }}>
              {isMulti ? `Creators receive (${item.recipientCount})` : "Creator receives"}
            </Typography>
            {youPaidFee && <Typography variant="caption" color="success.main" sx={{ fontSize: fsSecondary }}>Full amount — fee covered by you</Typography>}
            {creatorPaidFee && <Typography variant="caption" color="text.secondary" sx={{ fontSize: fsSecondary }}>Net after vendor-absorbed fee</Typography>}
            {!feeKnown && <Typography variant="caption" color="text.secondary" sx={{ fontSize: fsSecondary }}>Estimated</Typography>}
          </Box>
          <Typography variant="body2" fontWeight={600} color="success.main" sx={{ fontSize: fsPrimary }}>
            {currency.format(creatorsNetAmount)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: fsPrimary }}>
              Platform Fee ({fb.feePercentage}%)
            </Typography>
            {youPaidFee && <Typography variant="caption" color="text.secondary" sx={{ fontSize: fsSecondary }}>Billed to you on top of invoice</Typography>}
            {creatorPaidFee && <Typography variant="caption" color="text.secondary" sx={{ fontSize: fsSecondary }}>Absorbed by vendors — their choice</Typography>}
          </Box>
          <Typography variant="body2" fontWeight={600} color="warning.main" sx={{ fontSize: fsPrimary }}>
            {youPaidFee ? "+" : "−"}{currency.format(platformFee)}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {youPaidFee ? "Total Charged to You" : "Invoice Amount"}
          </Typography>
          <Typography variant="h6" fontWeight={700} color="warning.main">
            {currency.format(clientPays)}
          </Typography>
        </Box>
        {feeKnown && (
          <Alert
            severity={youPaidFee ? "success" : "info"}
            sx={{ py: 0.5 }}
            icon={youPaidFee ? <CheckCircleIcon fontSize="small" /> : <InfoIcon fontSize="small" />}
          >
            <Typography variant="caption" sx={{ fontSize: fsSecondary }}>
              {youPaidFee
                ? "You covered the platform fee — creators receive the full invoice amount."
                : "Vendors opted to absorb the platform fee. Your total charge is the invoice face value only."}
            </Typography>
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CONNECTED PAYOUTS SECTION
// ─────────────────────────────────────────────────────────────────────────────

const ConnectedPayoutsSection = ({ item, isMobile }) => {
  if (!item.isMerged) return null;
  const payouts = item.payoutDetailsArray || (item.payoutDetails ? [item.payoutDetails] : []);
  if (payouts.length === 0) return null;

  const isMulti = payouts.length > 1;
  const firstPayout = payouts[0];
  const isInstant = firstPayout?.payment_timing === "instant" || firstPayout?.blitzpay;

  return (
    <CollapsibleSection
      title={isMulti ? `Connected Payouts (${payouts.length} creators)` : "Connected Payout"}
      icon={isMulti ? <PeopleIcon /> : <LinkIcon />}
      defaultExpanded={true}
      isMobile={isMobile}
    >
      <Alert severity="success" sx={{ mb: 2, py: 0.5 }}>
        <Typography variant="body2" sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}>
          <strong>{isMulti ? "Batch merged record:" : "Merged record:"}</strong>{" "}
          {isMulti
            ? `This BlitzPay batch invoice and all ${payouts.length} creator payouts are shown as a single entry.`
            : "This BlitzPay invoice and its creator payout are shown as a single entry."}
        </Typography>
      </Alert>

      {isMulti && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: alpha("#1976d2", 0.06), border: "1px solid", borderColor: "primary.light" }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary">Recipients</Typography>
              <Typography variant="body2" fontWeight={700}>{payouts.length} creators</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Total Net Paid</Typography>
              <Typography variant="body2" fontWeight={700} color="success.main">
                {currency.format(item.totalPayoutNetAmount || 0)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Payment Timing</Typography>
              <Typography variant="body2" fontWeight={700}>{isInstant ? "Instant (BlitzPay)" : "Net 30"}</Typography>
            </Box>
          </Stack>
        </Box>
      )}

      {isMulti ? (
        <List dense disablePadding>
          {payouts.map((pd, idx) => (
            <React.Fragment key={pd.payout_id || idx}>
              {idx > 0 && <Divider />}
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 1 }}>
                <ListItemAvatar sx={{ minWidth: 36 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: "0.7rem" }}>
                    {(pd.name || pd.creator_id || "?")[0].toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}>
                        {pd.name || pd.creator_id || "Creator"}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                        <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}>
                          {currency.format(pd.net_amount || 0)}
                        </Typography>
                        {getStatusChip(pd.status, true)}
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {pd.creator_id && <Typography variant="caption" color="text.secondary">@{pd.creator_id}</Typography>}
                      {pd.payout_method && <Typography variant="caption" color="text.secondary">· {pd.payout_method}</Typography>}
                      {pd.payout_date && <Typography variant="caption" color="text.secondary">· {new Date(pd.payout_date).toLocaleDateString()}</Typography>}
                    </Stack>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableBody>
              {payouts[0].name && <InfoRow label="Creator" value={payouts[0].name} icon={<PersonIcon />} highlight isMobile={isMobile} />}
              {payouts[0].creator_id && <InfoRow label="Creator ID" value={`@${payouts[0].creator_id}`} icon={<InfoIcon />} isMobile={isMobile} />}
              <InfoRow label="Payout Status" value={payouts[0].status || "—"} icon={<CheckCircleIcon />} isMobile={isMobile} />
              <InfoRow label="Payment Timing" value={isInstant ? "Instant (BlitzPay)" : "Net 30"} icon={isInstant ? <SpeedIcon /> : <ScheduleIcon />} isMobile={isMobile} />
              {payouts[0].net_amount != null && <InfoRow label="Creator Receives" value={currency.format(payouts[0].net_amount)} icon={<AttachMoneyIcon />} highlight isMobile={isMobile} />}
              {payouts[0].payout_method && <InfoRow label="Payout Method" value={payouts[0].payout_method} icon={<CreditCardIcon />} isMobile={isMobile} />}
              {payouts[0].payout_date && <InfoRow label="Payout Date" value={formatDate(payouts[0].payout_date)} icon={<AccessTimeIcon />} isMobile={isMobile} />}
              {payouts[0].po_number && <InfoRow label="Payout Reference" value={payouts[0].po_number} icon={<DescriptionIcon />} isMobile={isMobile} />}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </CollapsibleSection>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN DIALOG
// ─────────────────────────────────────────────────────────────────────────────

const UnifiedDetailsDialog = ({ open, onClose, item }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);

  const [externalPoNumber, setExternalPoNumber] = useState("");
  const [savedExternalPoNumber, setSavedExternalPoNumber] = useState("");
  const [externalPoEditing, setExternalPoEditing] = useState(false);
  const [externalPoSaving, setExternalPoSaving] = useState(false);
  const [externalPoError, setExternalPoError] = useState("");
  const [externalPoSuccess, setExternalPoSuccess] = useState("");
  const [erpSystemName, setErpSystemName] = useState("");

  useEffect(() => {
    if (!open || !item?.id) return;
    const nextExternalPoNumber = item.external_po_number || "";
    setExternalPoNumber(nextExternalPoNumber);
    setSavedExternalPoNumber(nextExternalPoNumber);
    setErpSystemName(item.erp_system || item.vendor_system_name || "");
    setExternalPoError("");
    setExternalPoSuccess("");
    setExternalPoEditing(false);
  }, [open, item]);

  if (!item) return null;

  const itemType   = categorizeItem(item);
  const typeConfig = getTypeConfig(itemType);
  const accent     = typeConfig.accent;

  const isIncomingBBC = itemType === "bbc-split-incoming";
  const isOutgoingBBC = itemType === "bbc-split-outgoing";
  const isAnyBBC      = isIncomingBBC || isOutgoingBBC;

  // Derive accent colors for BBC types
  const accentMain   = isAnyBBC ? (isIncomingBBC ? YG : Y) : null;
  const accentDeep   = isAnyBBC ? (isIncomingBBC ? YG_DEEP : Y_DEEP) : null;
  const accentTint   = isAnyBBC ? (isIncomingBBC ? YG_TINT : Y_TINT) : null;
  const accentBorder = isAnyBBC ? (isIncomingBBC ? YG_BORDER : Y_BORDER) : null;
  const accentInk    = isAnyBBC ? (isIncomingBBC ? YG_INK : Y_INK) : null;
  const accentLight  = isAnyBBC ? (isIncomingBBC ? YG_LIGHT : Y_LIGHT) : null;

  const status   = item.status || item.invoice_status || "Unknown";
  const amount   = Number(item.amount || item.amount_due || item.net_amount || 0);
  const isBlitzPayTiming = item.blitzpay || item.payment_timing === "instant";
  const showExternalPO = itemType === "blitzpay";
  const isTransfer    = itemType === "transfer";
  const isWithdrawal  = itemType === "withdrawal";
  const isCredit      = item.direction === "credit";
  const isMulti       = item.isMultiRecipient && item.recipientCount > 1;

  const feePayer       = deriveFeePayer(item);
  const youPaidFee     = feePayer === "me";
  const creatorPaidFee = feePayer === "creator";

  const fb = item.feeBreakdown;
  const invoiceAmount = fb?.invoiceAmount  || amount;
  const platformFee   = fb?.platformFee    || 0;
  const totalCharge   = fb?.totalCharge    ?? (invoiceAmount + platformFee);

  const creatorsNetAmount = (() => {
    if (!fb?.isFunded) return amount;
    if (item.totalPayoutNetAmount > 0) return item.totalPayoutNetAmount;
    if (youPaidFee)     return invoiceAmount;
    if (creatorPaidFee) return invoiceAmount - platformFee;
    return invoiceAmount;
  })();

  const clientPays = fb?.isFunded ? (youPaidFee ? totalCharge : invoiceAmount) : amount;

  const entityInfo    = getEntityInfo(item, itemType);
  const invoiceRoute  = getInvoiceRoute(item, itemType);
  const hasInvoicePage = invoiceRoute !== null;

  // Payment term label for BBC
  const termLabel = (days) =>
    days == null ? null : days === 0 ? "Due on receipt" : `Net ${days}`;

  const dialogTitle = (() => {
    if (isIncomingBBC) return "Blitz Split Received";
    if (isOutgoingBBC) return "Blitz Split Sent";
    if (!item.isMerged) return typeConfig.label;
    if (isMulti) return `BlitzPay · ${item.recipientCount} Creators`;
    return "BlitzPay + Payout";
  })();

  // ── BBC amount box styling ───────────────────────────────────────────────
  const amountBannerBg    = isAnyBBC ? accentMain : `${typeConfig.color}.main`;
  const amountBannerColor = isAnyBBC ? accentInk  : "white";

  const handleSaveExternalPO = async () => {
    setExternalPoSaving(true);
    setExternalPoError("");
    setExternalPoSuccess("");
    try {
      const res = await client.invoices.updateExternalPO(item.id, {
        external_po_number: externalPoNumber.trim() || null,
      });
      const next = res.external_po_number || "";
      setExternalPoNumber(next);
      setSavedExternalPoNumber(next);
      setExternalPoSuccess("External PO updated successfully");
      setExternalPoEditing(false);
      setTimeout(() => setExternalPoSuccess(""), 3000);
    } catch (err) {
      setExternalPoError(err.response?.data?.error || "Failed to save external PO");
    } finally {
      setExternalPoSaving(false);
    }
  };

  const handleCancelExternalPOEdit = () => {
    setExternalPoEditing(false);
    setExternalPoError("");
    setExternalPoNumber(savedExternalPoNumber);
  };

  // ── Invoice preview card ─────────────────────────────────────────────────
  const InvoicePreviewCard = () => (
    <Card
      sx={{
        border: "2px solid",
        borderColor: isAnyBBC ? accentBorder : `${typeConfig.color}.main`,
        bgcolor: isAnyBBC ? accentTint : typeConfig.bgColor(theme),
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
          <Avatar sx={{ width: 44, height: 44, bgcolor: isAnyBBC ? accentMain : `${typeConfig.color}.main` }}>
            {React.cloneElement(typeConfig.icon, { sx: { fontSize: 22, color: isAnyBBC ? accentInk : "white" } })}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2, color: isAnyBBC ? accentInk : "text.primary" }}>
              {dialogTitle}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
              {getStatusChip(status, false, itemType)}
              {item.isMerged && (
                <Chip
                  label={isMulti ? `${item.recipientCount} creators` : "Merged"}
                  size="small" color="success" variant="outlined"
                  sx={{ height: 24, fontSize: "0.7rem", fontWeight: 600 }}
                />
              )}
              {isAnyBBC && item.payment_term != null && termLabel(item.payment_term) && (
                <Chip
                  label={termLabel(item.payment_term)}
                  size="small" variant="outlined"
                  sx={{ height: 24, fontSize: "0.7rem", fontWeight: 700, borderColor: accentBorder, color: accentDeep }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* BBC context banner */}
        <BBCContextBanner item={item} itemType={itemType} />

        {/* Reference grid */}
        <Box sx={{
          mb: 2.5, p: 1.5, bgcolor: "white", borderRadius: 1.5,
          border: "1px solid", borderColor: isAnyBBC ? accentBorder : "divider",
        }}>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                {isAnyBBC ? "Blitz Split #" : "Blitz Invoice #"}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {item.po_number || item.unique_code || `#${item.id}`}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Date</Typography>
              <Typography variant="body2">
                {item.created_at || item.date ? new Date(item.created_at || item.date).toLocaleDateString() : "N/A"}
              </Typography>
            </Grid>

            {/* BBC-specific: recipient count */}
            {isAnyBBC && entityInfo.recipientCount && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Contractors</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: accentDeep }}>
                  {entityInfo.recipientCount} contractor{entityInfo.recipientCount !== 1 ? "s" : ""}
                  {entityInfo.preCount > 0 && ` · ${entityInfo.preCount} ⚡ pre-accepted`}
                </Typography>
              </Grid>
            )}

            {showExternalPO && externalPoNumber && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  {erpSystemName ? `${erpSystemName} PO` : "Client PO"}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={externalPoNumber} size="small" color="info" variant="outlined" />
                </Box>
              </Grid>
            )}
            {isMulti && item.recipientCount && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Recipients</Typography>
                <Typography variant="body2" fontWeight={600}>{item.recipientCount} creators</Typography>
              </Grid>
            )}
            {!isMulti && !isAnyBBC && entityInfo.subtitle && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  {itemType === "payout" ? "Creator" : "Client"}
                </Typography>
                <Typography variant="body2" fontWeight={600}>{entityInfo.name}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Fee breakdown (non-BBC only — per BBCSplitApprovalPage: no fee for approver) */}
        {!isAnyBBC && fb?.isFunded && (
          <FeeBreakdownBlock item={item} isMulti={isMulti} feePayer={feePayer} isMobile={isMobile} />
        )}

        {/* BBC no-fee notice */}
        {isAnyBBC && (
          <Box sx={{ mb: 2.5, p: 1.25, borderRadius: 1.5, bgcolor: accentTint, border: `1px solid ${accentBorder}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CheckCircleIcon sx={{ fontSize: 14, color: accentMain }} />
              <Typography variant="caption" sx={{ color: accentDeep, fontWeight: 600, fontSize: "0.72rem" }}>
                No additional fee charged — Blitz discount rate borne by invoicing party
              </Typography>
            </Box>
          </Box>
        )}

        {/* Total amount banner */}
        <Box sx={{
          p: 2.5, bgcolor: amountBannerBg, borderRadius: 2,
          color: amountBannerColor, textAlign: "center",
        }}>
          <Typography variant="caption" display="block" sx={{ opacity: 0.85, fontWeight: 600, letterSpacing: "0.05em", mb: 0.5 }}>
            {isAnyBBC
              ? (isIncomingBBC ? "INVOICE TOTAL" : "SPLIT AMOUNT")
              : fb?.isFunded
                ? youPaidFee ? "TOTAL CHARGED TO YOU" : "INVOICE AMOUNT"
                : isTransfer ? (isCredit ? "CREDITED" : "DEBITED")
                : isWithdrawal ? "WITHDRAWN"
                : isMulti ? `TOTAL (${item.recipientCount} CREATORS)`
                : "TOTAL AMOUNT"}
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {isTransfer && (isCredit ? "+" : "−")}
            {isWithdrawal && "−"}
            {currency.format(isAnyBBC ? amount : (fb?.isFunded ? clientPays : amount))}
          </Typography>

          {/* BBC sub-line */}
          {isAnyBBC && entityInfo.recipientCount && (
            <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.9 }}>
              Split across {entityInfo.recipientCount} contractor{entityInfo.recipientCount !== 1 ? "s" : ""}
            </Typography>
          )}

          {/* Non-BBC fee sub-line */}
          {!isAnyBBC && fb?.isFunded && (
            <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.9 }}>
              {isMulti ? `${item.recipientCount} creators` : "Creator"} receives:{" "}
              <strong>{currency.format(creatorsNetAmount)}</strong>
              {youPaidFee ? " (full amount)" : creatorPaidFee ? " (after vendor-absorbed fee)" : ""}
            </Typography>
          )}
          {!isAnyBBC && !fb?.isFunded && isMulti && item.totalPayoutNetAmount > 0 && (
            <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.9 }}>
              Creators receive: {currency.format(item.totalPayoutNetAmount)}
            </Typography>
          )}
          {!isAnyBBC && !fb?.isFunded && !isMulti && item.isMerged && item.payoutDetails?.net_amount != null && (
            <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.9 }}>
              Creator receives: {currency.format(item.payoutDetails.net_amount)}
            </Typography>
          )}
        </Box>

        {hasInvoicePage && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<OpenInNewIcon />}
              onClick={() => navigate(invoiceRoute)}
              sx={{
                borderColor: isAnyBBC ? accentBorder : `${typeConfig.color}.main`,
                color: isAnyBBC ? accentDeep : `${typeConfig.color}.main`,
                fontWeight: 600,
                "&:hover": {
                  bgcolor: isAnyBBC ? accentTint : alpha(theme.palette[typeConfig.color]?.main || "#000", 0.05),
                },
              }}
            >
              View Full Invoice
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // ── External PO section ──────────────────────────────────────────────────
  const renderExternalPOSection = () => {
    if (!showExternalPO) return null;
    return (
      <CollapsibleSection title="Client ERP / AP Reconciliation" icon={<AssignmentIcon />} defaultExpanded={true} isMobile={isMobile}>
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}>
              <strong>External PO Tracking:</strong> Store the client's PO number from their ERP/AP system for reconciliation.
            </Typography>
          </Alert>
          {externalPoEditing ? (
            <Box>
              <TextField
                fullWidth
                label={erpSystemName ? `${erpSystemName} PO Number` : "External PO Number"}
                value={externalPoNumber}
                onChange={(e) => setExternalPoNumber(e.target.value)}
                placeholder={erpSystemName ? `e.g., ${erpSystemName}-12345` : "e.g., SAP-12345"}
                helperText={erpSystemName ? `Client's PO in ${erpSystemName}` : "Client ERP/AP PO for reconciliation"}
                size={isMobile ? "small" : "medium"}
                disabled={externalPoSaving}
                error={!!externalPoError}
                sx={{ mb: 2 }}
              />
              {externalPoError && <Alert severity="error" sx={{ mb: 2 }}>{externalPoError}</Alert>}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="contained" onClick={handleSaveExternalPO} disabled={externalPoSaving}
                  startIcon={externalPoSaving ? <CircularProgress size={16} /> : <SaveIcon />} size={isMobile ? "small" : "medium"}>
                  Save
                </Button>
                <Button variant="outlined" onClick={handleCancelExternalPOEdit} disabled={externalPoSaving}
                  startIcon={<CancelIcon />} size={isMobile ? "small" : "medium"}>
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <InfoRow label={erpSystemName ? `${erpSystemName} PO` : "External PO"} value={externalPoNumber || "—"} icon={<AssignmentIcon />} highlight={!!externalPoNumber} isMobile={isMobile} />
                    {erpSystemName && <InfoRow label="ERP System" value={erpSystemName} icon={<BusinessIcon />} isMobile={isMobile} />}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={() => setExternalPoEditing(true)} startIcon={<EditIcon />} size={isMobile ? "small" : "medium"} fullWidth={isMobile}>
                  {externalPoNumber ? "Edit External PO" : "Add External PO"}
                </Button>
              </Box>
            </Box>
          )}
        </>
      </CollapsibleSection>
    );
  };

  // ── Detail sections ──────────────────────────────────────────────────────
  const renderDetailSections = () => (
    <>
      {renderExternalPOSection()}

      {/* BBC Roster — shown when BBC split */}
      {isAnyBBC && <BBCRosterSection item={item} itemType={itemType} isMobile={isMobile} />}

      {/* Connected payouts (BlitzPay merged, non-BBC) */}
      {!isAnyBBC && <ConnectedPayoutsSection item={item} isMobile={isMobile} />}

      {/* Entity info */}
      {!isTransfer && !isWithdrawal && (
        <CollapsibleSection
          title="Entity Information"
          icon={<PersonIcon />}
          defaultExpanded={true}
          isMobile={isMobile}
          accentColor={accentMain}
        >
          <TableContainer>
            <Table size="small">
              <TableBody>
                <InfoRow label={isIncomingBBC ? "To you" : isOutgoingBBC ? "Client" : "Name"} value={entityInfo.name} icon={<PersonIcon />} highlight accentColor={accentMain} isMobile={isMobile} />
                {entityInfo.subtitle && (
                  <InfoRow label={isAnyBBC ? "Contractors" : "Identifier"} value={entityInfo.subtitle} icon={<InfoIcon />} isMobile={isMobile} />
                )}
                {entityInfo.email && <InfoRow label="Email" value={entityInfo.email} icon={<InfoIcon />} isMobile={isMobile} />}
                {isMulti && item.recipientCount && (
                  <InfoRow label="Recipients" value={`${item.recipientCount} creators`} icon={<GroupIcon />} isMobile={isMobile} />
                )}
                {isAnyBBC && item.payment_term != null && (
                  <InfoRow label="Payment Terms" value={termLabel(item.payment_term) || "Standard"} icon={<ScheduleIcon />} isMobile={isMobile} />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CollapsibleSection>
      )}

      {/* Transaction details */}
      <CollapsibleSection
        title="Transaction Details"
        icon={<DescriptionIcon />}
        defaultExpanded={true}
        isMobile={isMobile}
        accentColor={accentMain}
      >
        <TableContainer>
          <Table size="small">
            <TableBody>
              <InfoRow
                label={isAnyBBC ? "Blitz Split #" : "Blitz PO / Reference"}
                value={item.po_number || item.unique_code || `#${item.id}`}
                icon={<DescriptionIcon />}
                isMobile={isMobile}
              />
              <InfoRow label="Status" value={status} icon={<InfoIcon />} isMobile={isMobile} />
              <InfoRow
                label="Amount"
                value={`${isTransfer ? (isCredit ? "+" : "−") : isWithdrawal ? "−" : ""}${currency.format(amount)}`}
                icon={<AttachMoneyIcon />}
                highlight
                accentColor={accentMain}
                isMobile={isMobile}
              />

              {/* BBC: no fee rows — fee is on the SME's side */}
              {isAnyBBC && (
                <InfoRow
                  label="Fee to You"
                  value="None — Blitz discount borne by invoicing party"
                  icon={<CheckCircleIcon />}
                  isMobile={isMobile}
                />
              )}

              {/* Non-BBC fee breakdown rows */}
              {!isAnyBBC && fb?.isFunded && (
                <>
                  <InfoRow
                    label={isMulti ? `Creators receive (${item.recipientCount})` : "Creator receives"}
                    value={currency.format(creatorsNetAmount)}
                    icon={<AttachMoneyIcon />}
                    highlight
                    isMobile={isMobile}
                  />
                  <InfoRow
                    label={`Platform Fee (${fb.feePercentage}%) — ${youPaidFee ? "paid by you" : creatorPaidFee ? "absorbed by vendors" : "pending"}`}
                    value={`${youPaidFee ? "+" : "−"}${currency.format(platformFee)}`}
                    icon={<AttachMoneyIcon />}
                    isMobile={isMobile}
                  />
                  <InfoRow
                    label={youPaidFee ? "Total Charged to You" : "Invoice Face Value"}
                    value={currency.format(clientPays)}
                    icon={<AttachMoneyIcon />}
                    highlight
                    isMobile={isMobile}
                  />
                </>
              )}

              {!isAnyBBC && isMulti && item.totalPayoutNetAmount > 0 && !fb?.isFunded && (
                <InfoRow label="Total Net to Creators" value={currency.format(item.totalPayoutNetAmount)} icon={<AttachMoneyIcon />} highlight isMobile={isMobile} />
              )}

              {isTransfer && (
                <InfoRow label="Direction" value={isCredit ? "Credit (Money In)" : "Debit (Money Out)"} icon={isCredit ? <ArrowDownIcon /> : <ArrowUpIcon />} isMobile={isMobile} />
              )}
              {isWithdrawal && item.payment_service && (
                <InfoRow label="Payment Service" value={item.payment_service} icon={<AccountBalanceIcon />} isMobile={isMobile} />
              )}
              {itemType === "payout" && (
                <>
                  {item.interest_rate > 0 && <InfoRow label="Fee Rate" value={`${item.interest_rate}%`} icon={<InfoIcon />} isMobile={isMobile} />}
                  {item.fee_amount > 0 && <InfoRow label="Fee Amount" value={currency.format(item.fee_amount)} icon={<AttachMoneyIcon />} isMobile={isMobile} />}
                  <InfoRow label="Net Amount" value={currency.format(item.net_amount || amount)} icon={<AttachMoneyIcon />} highlight isMobile={isMobile} />
                  <InfoRow label="Payment Timing" value={isBlitzPayTiming ? "Instant (BlitzPay)" : "Net 30"} icon={isBlitzPayTiming ? <SpeedIcon /> : <ScheduleIcon />} isMobile={isMobile} />
                </>
              )}
              {item.campaign_name && !isTransfer && !isWithdrawal && (
                <InfoRow label="Campaign" value={item.campaign_name} icon={<InfoIcon />} isMobile={isMobile} />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CollapsibleSection>

      {/* Timeline */}
      <CollapsibleSection title="Timeline" icon={<AccessTimeIcon />} defaultExpanded={true} isMobile={isMobile} accentColor={accentMain}>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {(item.created_at || item.date) && (
                <InfoRow label="Created" value={formatDate(item.created_at || item.date)} icon={<AccessTimeIcon />} isMobile={isMobile} />
              )}
              {item.payout_date && (
                <InfoRow label="Payout Date" value={formatDate(item.payout_date)} icon={<AccessTimeIcon />} isMobile={isMobile} />
              )}
              {item.approved_at && (
                <InfoRow label="Approved" value={formatDate(item.approved_at)} icon={<CheckCircleIcon />} isMobile={isMobile} />
              )}
              {item.date_paid && (
                <InfoRow label="Paid" value={formatDate(item.date_paid)} icon={<CheckCircleIcon />} isMobile={isMobile} />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CollapsibleSection>

      {(itemType === "payout" || isWithdrawal) && item.payout_method && (
        <CollapsibleSection title="Payment Information" icon={<CreditCardIcon />} defaultExpanded={false} isMobile={isMobile}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                <InfoRow label="Payment Method" value={item.payout_method} icon={<CreditCardIcon />} isMobile={isMobile} />
                {item.withdrawal_info && (
                  <>
                    <InfoRow label="Withdrawal Status" value={item.withdrawal_info.status} icon={<InfoIcon />} isMobile={isMobile} />
                    <InfoRow label="Payment Service" value={item.withdrawal_info.payment_service || "N/A"} icon={<AccountBalanceIcon />} isMobile={isMobile} />
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CollapsibleSection>
      )}

      {(item.notes || item.description || item.brief) && (
        <CollapsibleSection title="Notes" icon={<DescriptionIcon />} defaultExpanded={false} isMobile={isMobile}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "0.8rem" : undefined }}>
            {item.notes || item.description || item.brief}
          </Typography>
        </CollapsibleSection>
      )}
    </>
  );

  // ── Header bar — BBC gets yellow gradient bar ────────────────────────────
  const headerAccentBar = isAnyBBC ? (
    <Box sx={{ height: 4, background: `linear-gradient(90deg, ${accentMain} 0%, ${alpha(accentMain, 0.35)} 100%)`, flexShrink: 0 }} />
  ) : null;

  // ── MOBILE LAYOUT ────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 0, m: 0,
            paddingTop: "env(safe-area-inset-top, 0px)",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            height: "100vh", maxHeight: "100vh",
          },
        }}
      >
        {/* Mobile header */}
        <Box sx={{
          pb: 2, pt: "max(16px, env(safe-area-inset-top, 0px))", px: 2,
          bgcolor: isAnyBBC ? accentTint : "background.paper",
          borderBottom: "1px solid",
          borderColor: isAnyBBC ? accentBorder : "divider",
        }}>
          {isAnyBBC && (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.4, borderRadius: 1.5, bgcolor: accentMain, mb: 1.25 }}>
              {isIncomingBBC ? <CallSplitIcon sx={{ fontSize: 12, color: accentInk }} /> : <BoltIcon sx={{ fontSize: 12, color: accentInk }} />}
              <Typography sx={{ fontWeight: 900, color: accentInk, fontSize: "0.67rem", letterSpacing: "0.06em" }}>
                BLITZ SPLIT
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
              <Avatar sx={{ width: 38, height: 38, bgcolor: isAnyBBC ? accentMain : `${typeConfig.color}.main`, flexShrink: 0 }}>
                {React.cloneElement(typeConfig.icon, { sx: { fontSize: 18, color: isAnyBBC ? accentInk : "white" } })}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: isAnyBBC ? accentInk : "text.primary" }}>
                  {dialogTitle}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, mt: 0.25, flexWrap: "wrap" }}>
                  {getStatusChip(status, true, itemType)}
                  {isAnyBBC && item.payment_term != null && termLabel(item.payment_term) && (
                    <Chip label={termLabel(item.payment_term)} size="small" variant="outlined"
                      sx={{ height: 20, fontSize: "0.63rem", fontWeight: 700, borderColor: accentBorder, color: accentDeep }} />
                  )}
                  {!isAnyBBC && item.isMerged && (
                    <Chip label={isMulti ? `${item.recipientCount} creators` : "Merged"} size="small" color="success" variant="outlined"
                      sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }} />
                  )}
                </Box>
              </Box>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary", bgcolor: alpha(theme.palette.action.hover, 0.1) }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: 2, overflowY: "auto", flex: 1, WebkitOverflowScrolling: "touch", paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}>
          <Button
            fullWidth variant="outlined"
            onClick={() => setShowInvoicePreview(!showInvoicePreview)}
            endIcon={showInvoicePreview ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              mb: 2, mt: 2, textTransform: "none",
              ...(isAnyBBC && { borderColor: accentBorder, color: accentDeep, "&:hover": { bgcolor: accentTint } }),
            }}
          >
            {showInvoicePreview ? "Hide" : "Show"} Invoice Preview
          </Button>
          <Collapse in={showInvoicePreview}>
            <Box sx={{ mb: 2 }}><InvoicePreviewCard /></Box>
          </Collapse>
          {hasInvoicePage && (
            <Alert
              severity="info" sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" startIcon={<OpenInNewIcon fontSize="small" />} onClick={() => navigate(invoiceRoute)} sx={{ fontSize: "0.75rem" }}>
                  Open
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}><strong>Full invoice available</strong></Typography>
            </Alert>
          )}
          {renderDetailSections()}
        </Box>

        <Box sx={{
          px: 2, pt: 2,
          pb: "calc(98px + 16px + env(safe-area-inset-bottom, 0px))",
          bgcolor: isAnyBBC ? accentTint : "grey.50",
          borderTop: "1px solid",
          borderColor: isAnyBBC ? accentBorder : "divider",
          display: "flex", flexDirection: "column-reverse", gap: 1,
        }}>
          {hasInvoicePage && (
            <Button
              onClick={() => navigate(invoiceRoute)}
              variant="outlined" size="large" fullWidth
              startIcon={<OpenInNewIcon fontSize="small" />}
              sx={{
                fontSize: "0.875rem", textTransform: "none", fontWeight: 600,
                ...(isAnyBBC && { borderColor: accentBorder, color: accentDeep }),
              }}
            >
              View Full Invoice
            </Button>
          )}
          <Button
            onClick={onClose} variant="contained" size="large" fullWidth
            sx={{
              fontSize: "0.875rem", textTransform: "none", fontWeight: 600,
              ...(isAnyBBC && { bgcolor: accentMain, color: accentInk, "&:hover": { bgcolor: isIncomingBBC ? YG_DARK : Y_DARK } }),
            }}
          >
            Close
          </Button>
        </Box>

        <Snackbar open={!!externalPoSuccess} autoHideDuration={3000} onClose={() => setExternalPoSuccess("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity="success" onClose={() => setExternalPoSuccess("")}>{externalPoSuccess}</Alert>
        </Snackbar>
      </Dialog>
    );
  }

  // ── DESKTOP LAYOUT ───────────────────────────────────────────────────────
  if (!open) return null;

  return (
    <Fade in={open} timeout={300}>
      <Box sx={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        bgcolor: "background.default", zIndex: theme.zIndex.modal + 1,
        display: "flex", flexDirection: "column",
      }}>
        {/* Desktop header */}
        <Box sx={{
          height: 72, borderBottom: "1px solid",
          borderColor: isAnyBBC ? accentBorder : "divider",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 4,
          bgcolor: isAnyBBC ? accentTint : "background.paper",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* BBC badge */}
            {isAnyBBC && (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.5, borderRadius: 2, bgcolor: accentMain }}>
                {isIncomingBBC ? <CallSplitIcon sx={{ fontSize: 14, color: accentInk }} /> : <BoltIcon sx={{ fontSize: 14, color: accentInk }} />}
                <Typography sx={{ fontWeight: 900, color: accentInk, fontSize: "0.72rem", letterSpacing: "0.06em" }}>BLITZ SPLIT</Typography>
              </Box>
            )}
            <Box sx={{
              width: 44, height: 44, borderRadius: 2,
              bgcolor: isAnyBBC ? accentMain : `${typeConfig.color}.main`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {React.cloneElement(typeConfig.icon, { sx: { fontSize: 24, color: isAnyBBC ? accentInk : "white" } })}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: isAnyBBC ? accentInk : "text.primary" }}>
                {dialogTitle}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25, flexWrap: "wrap" }}>
                <Typography variant="caption" color="text.secondary">
                  {item.po_number || item.unique_code || `#${item.id}`}
                </Typography>
                {getStatusChip(status, true, itemType)}
                {isAnyBBC && item.payment_term != null && termLabel(item.payment_term) && (
                  <Chip label={termLabel(item.payment_term)} size="small" variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, borderColor: accentBorder, color: accentDeep }} />
                )}
                {!isAnyBBC && item.isMerged && (
                  <Chip
                    label={isMulti ? `${item.recipientCount} creators merged` : "Invoice + Payout merged"}
                    size="small" color="success" variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
                  />
                )}
                {!isAnyBBC && feePayer && fb?.isFunded && (
                  <Chip
                    label={youPaidFee ? "You covered the fee" : "Fee absorbed by vendors"}
                    size="small" color={youPaidFee ? "success" : "warning"} variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ bgcolor: "action.hover", "&:hover": { bgcolor: "action.selected" } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Yellow accent gradient bar for BBC */}
        {headerAccentBar}

        {/* Desktop body */}
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Left panel */}
          <Box sx={{
            width: 400,
            borderRight: "1px solid",
            borderColor: isAnyBBC ? accentBorder : "divider",
            overflowY: "auto",
            bgcolor: isAnyBBC ? alpha(accentMain, 0.03) : "background.paper",
          }}>
            <Box sx={{ p: 3 }}><InvoicePreviewCard /></Box>
          </Box>

          {/* Right panel */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 4 }}>
            {hasInvoicePage && (
              <Alert
                severity="info" sx={{ mb: 3 }}
                action={
                  <Button color="inherit" size="small" startIcon={<OpenInNewIcon />} onClick={() => navigate(invoiceRoute)}>
                    Open
                  </Button>
                }
              >
                <Typography variant="body2">
                  <strong>Full invoice available</strong> — view complete details and supporting documents.
                </Typography>
              </Alert>
            )}
            {renderDetailSections()}
            <Box sx={{
              position: "sticky", bottom: 0, pt: 3, pb: 2,
              bgcolor: "background.default",
              borderTop: "1px solid",
              borderColor: isAnyBBC ? accentBorder : "divider",
              mt: 4,
            }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                {hasInvoicePage && (
                  <Button
                    onClick={() => navigate(invoiceRoute)}
                    variant="outlined" size="large"
                    startIcon={<OpenInNewIcon />}
                    sx={{
                      px: 4, textTransform: "none", fontWeight: 600,
                      ...(isAnyBBC && { borderColor: accentBorder, color: accentDeep }),
                    }}
                  >
                    View Full Invoice
                  </Button>
                )}
                <Button
                  onClick={onClose} variant="contained" size="large"
                  sx={{
                    flex: hasInvoicePage ? 0 : 1, px: 4, textTransform: "none", fontWeight: 600,
                    ...(isAnyBBC && {
                      bgcolor: accentMain, color: accentInk,
                      "&:hover": { bgcolor: isIncomingBBC ? YG_DARK : Y_DARK },
                    }),
                  }}
                >
                  Close
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        <Snackbar open={!!externalPoSuccess} autoHideDuration={3000} onClose={() => setExternalPoSuccess("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity="success" onClose={() => setExternalPoSuccess("")}>{externalPoSuccess}</Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default UnifiedDetailsDialog;